import React, { useEffect, useRef, useState } from 'react'
import './manage-item-card.styles.scss';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import ItemPrice from '@/components/SHARED/item-price/item-price.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faPen, faSlash } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import LayeredIcons from '@/components/SHARED/layered-icons/layered-icons.component';
import { faHand } from '@fortawesome/free-regular-svg-icons';
import { format } from 'date-fns';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { useSelector } from 'react-redux';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { ModalAlert } from '@/components/modal/modal.component';
import useAnalytics from '@/utils/useAnalytics';

const ManageItemCard = ({
    item,
    saleList,
    gameElements,
    gameStats,
    gameData,
    removeItemFromStore
}) => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const screenSize = useSelector(state => state.contentData.screenSize);
    const membership = useSelector(state => state.userData.membership);
    const [closed, setClosed] = useState([]);
    const [loading, setLoading] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);
    const { trackEvent, trackException } = useAnalytics();
    const navigate = useNavigate();
    const location = useLocation();
    const intRef = useRef();

    useEffect(() => {
        return () => {
            if (intRef.current) clearInterval(intRef.current);
        }
    }, [])

    useEffect(() => {
        if (!item) return;
        checkItemStatus();
        intRef.current = setInterval(() => {
            checkItemStatus();
        }, 60000);
    }, [item])

    function checkItemStatus() {
        const now = new Date().getTime();
        let closeArr = []
        if (!item.opt_available) closeArr.push('Item is marked as unavailable.')
        if (now < item.opt_dateOpen) closeArr.push(`Item opens on ${format(new Date(item.opt_dateOpen), 'Pp')}.`)
        if (now > item.opt_dateClose) closeArr.push(`Item closed on ${format(new Date(item.opt_dateClose), 'Pp')}.`)
        setClosed(closeArr);
    }

    async function toggleItemAvailability() {
        if (location.pathname.includes('/preview/manager/')) {
            setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
            return;
        }
        setLoading('available');
        let tempItem = { ...item };
        tempItem.opt_available = !item.opt_available;
        const res = await UseCloudFunction(
            'toggleElementAvailability',
            {
                'gameId': gameData.gameId,
                'appId': userProfile.appId,
                'elementData': tempItem,
            }
        )
        console.log(res);
        setLoading(null);
        if (res.error) {
            setAlertMessage(`Something went wrong: ${res.error}`);
            trackException(`Error toggling item availability: ${res.error}`);
            return;
        }
        trackEvent(
            'item_availability_toggled_from_store',
            'Game Manager',
            `Item ${item.id} availability toggled to ${tempItem.opt_available} for game ${gameData.gameId}.`,
            null,
            membership
        );
    }



    return (
        <div className={`manage-item-card g-list-item ${screenSize}`}>
            <div className='manage-item-grid'>
                <div className='store-item-icon-div'>
                    <ImageContainer 
                        src={item.icon} 
                        className='item-icon'
                        preview={true} />
                </div>
                <div className='store-item-text-div'>
                    <div className='item-name'>{item.name}</div>
                    <div className='item-desc'>{item.desc}</div>
                    {
                        (item.opt_itemType === 'contribution')
                            ?
                            <div className='item-price'>
                                Goal:
                                <ItemPrice
                                    item={{
                                        'opt_marketPrice': item.opt_contributionGoal,
                                    }}
                                    saleList={[]}
                                    gameElements={gameElements} />
                            </div>
                            :
                            <div className='item-price'>
                                Price:
                                <ItemPrice
                                    item={item}
                                    saleList={saleList}
                                    gameElements={gameElements}
                                />
                            </div>
                    }
                    {
                        (item.opt_itemType === 'lottery') &&
                        <div className='item-desc'>
                            Tickets Sold: {gameStats[item.id] ? gameStats[item.id].toLocaleString() : 0}
                        </div>
                    }
                    {
                        (closed) &&
                        <div className='access-list'>
                            {
                                closed.map(c => (
                                    <div key={c} className='meta access-item'>{c}</div>
                                ))
                            }
                        </div>
                    }
                    <div className='buttons'>
                        {
                            (loading === 'available')
                                ?
                                <button className='g-button med-btn'><ThreeDotsLoader /></button>
                                :
                                closed.length === 0
                                    ?
                                    <button
                                        className='g-button med-btn open'
                                        onClick={() => toggleItemAvailability()}
                                    >
                                        Available
                                        <FontAwesomeIcon icon={faHand} />
                                    </button>
                                    :
                                    closed.length === 1 && closed[0].includes('marked as unavailable')
                                        ?
                                        <button
                                            className='g-button med-btn closed'
                                            onClick={() => toggleItemAvailability()}
                                        >
                                            Closed
                                            <LayeredIcons
                                                icon1={faHand}
                                                icon2={faSlash}
                                            />
                                        </button>
                                        :
                                        closed.length >= 1
                                        &&
                                        <button
                                            className='g-button med-btn disabled'
                                        >
                                            Closed
                                            <LayeredIcons
                                                icon1={faHand}
                                                icon2={faSlash}
                                            />
                                        </button>
                        }
                        <button
                            className='g-button med-btn'
                            onClick={() => navigate(
                                item.opt_itemType === 'prizepack'
                                    ?
                                    `/manage/${gameData.path}/elements/prizepacks/edit/${item.path}`
                                    :
                                    `/manage/${gameData.path}/elements/items/edit/${item.path}`
                            )}
                        >
                            {screenSize !== 'mobile' && screenSize !== 'tablet' ? 'Edit' : ''}
                            <FontAwesomeIcon icon={faPen} />
                        </button>
                        {
                            (loading === 'remove')
                                ?
                                <button className='g-button med-btn'><ThreeDotsLoader /></button>
                                :
                                <button
                                    className='g-button med-btn'
                                    onClick={() => removeItemFromStore(item)}
                                    title='Remove Item from Store'
                                >
                                    {screenSize !== 'mobile' && screenSize !== 'tablet' ? 'Remove' : ''}
                                    <FontAwesomeIcon icon={faBan} />
                                </button>
                        }
                    </div>
                </div>

            </div>
            <ModalAlert
                show={alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage} />
        </div>
    )
}

export default ManageItemCard